/**
 * CRM typed API (Phase 7; bodies unified in the open-questions-#2 slice). Every
 * shape — input body, path params, resolved value — derives from the SERVER's
 * route defs via the type-only import below (erased at build; no server code
 * reaches the bundle). A server-side shape change breaks this file and its
 * callers at compile time.
 *
 * CRM's list endpoints declare page/pageSize/q (and activities' `type`) on their
 * `query` schemas, so callers' query objects are type-checked too. Input/query
 * types are the schemas' INPUT side, so defaulted fields (dealCreate's
 * valueCents/status, every list param) stay optional to callers.
 *
 * Deliberately NOT here:
 * - POST /deals/:id/attachments (uploadAttachment): multipart/form-data — the
 *   page sends it through `apiUpload` (the typed client is JSON-only).
 */
import { type RouteOutputOf, typedRoute } from "@d12labs/client";
import type { crmRouteDefs } from "../server/routes";

export const crmApi = {
	listContacts: typedRoute<typeof crmRouteDefs.listContacts>()("GET", "/api/plugins/crm/contacts"),
	createContact: typedRoute<typeof crmRouteDefs.createContact>()(
		"POST",
		"/api/plugins/crm/contacts",
	),
	contact: typedRoute<typeof crmRouteDefs.contact>()("GET", "/api/plugins/crm/contacts/:id"),
	updateContact: typedRoute<typeof crmRouteDefs.updateContact>()(
		"PATCH",
		"/api/plugins/crm/contacts/:id",
	),
	removeContact: typedRoute<typeof crmRouteDefs.removeContact>()(
		"DELETE",
		"/api/plugins/crm/contacts/:id",
	),
	sendEmail: typedRoute<typeof crmRouteDefs.sendEmail>()(
		"POST",
		"/api/plugins/crm/contacts/:id/email",
	),
	activities: typedRoute<typeof crmRouteDefs.activities>()(
		"GET",
		"/api/plugins/crm/contacts/:id/activities",
	),
	draftEmail: typedRoute<typeof crmRouteDefs.draftEmail>()(
		"POST",
		"/api/plugins/crm/contacts/:id/draft-email",
	),
	listDeals: typedRoute<typeof crmRouteDefs.listDeals>()("GET", "/api/plugins/crm/deals"),
	createDeal: typedRoute<typeof crmRouteDefs.createDeal>()("POST", "/api/plugins/crm/deals"),
	updateDeal: typedRoute<typeof crmRouteDefs.updateDeal>()("PATCH", "/api/plugins/crm/deals/:id"),
	removeDeal: typedRoute<typeof crmRouteDefs.removeDeal>()("DELETE", "/api/plugins/crm/deals/:id"),
	dealAttachments: typedRoute<typeof crmRouteDefs.dealAttachments>()(
		"GET",
		"/api/plugins/crm/deals/:id/attachments",
	),
	removeAttachment: typedRoute<typeof crmRouteDefs.removeAttachment>()(
		"DELETE",
		"/api/plugins/crm/deal-attachments/:id",
	),
	attachmentUrl: typedRoute<typeof crmRouteDefs.attachmentUrl>()(
		"GET",
		"/api/plugins/crm/deal-attachments/:id/url",
	),
};

/** The server's row/response shapes, derived — never redeclared by hand. */
export type Contact = RouteOutputOf<typeof crmRouteDefs.listContacts>["items"][number];
export type ContactWithDeals = RouteOutputOf<typeof crmRouteDefs.contact>;
export type Deal = RouteOutputOf<typeof crmRouteDefs.listDeals>[number];
export type Activity = RouteOutputOf<typeof crmRouteDefs.activities>["items"][number];
export type Attachment = RouteOutputOf<typeof crmRouteDefs.dealAttachments>[number];
